// Curated reference data (CAP languages) — a value list, not an entity.
export * from './cap-languages.js';
// ─── Core Value Objects ────────────────────────────────────────────────────
/**
 * Base class for all Value Objects.
 * Ensures immutability and structural equality.
 */
export class ValueObject {
    value;
    constructor(value) {
        this.validate(value);
        this.value = Object.freeze(value);
    }
    equals(other) {
        return JSON.stringify(this.value) === JSON.stringify(other.value);
    }
    getValue() {
        return this.value;
    }
    /** UN OBJETO DE VALOR NUNCA DEBE CONVERTIRSE EN «[object Object]».
     *
     *  Sin esto, `String(vo)` y `${vo}` devolvían esa cadena, y el resultado no
     *  es un texto feo en pantalla: es una CLAVE. El libro de notas usaba
     *  `String(studentId)` para indexar entregas por alumno, así que los 21
     *  alumnos de una sección caían en la misma clave y todos recibían la nota
     *  de una mezcla de todos. El error se veía como un nombre raro; lo que
     *  estaba mal eran las notas.
     *
     *  `.getValue()` sigue siendo la forma correcta de leer el valor. Esto es la
     *  red: cuando alguien igualmente interpole el objeto, que salga el dato y
     *  no una cadena que parece un dato. */
    toString() {
        // Los objetos de valor con un valor COMPUESTO (p. ej. `Money`, que envuelve
        // `{ amount, currency }`) volverían a dar «[object Object]` con un `String()`
        // a secas. Se serializan para que, si alguien igualmente los interpola, lo
        // que salga sea diagnosticable en vez de indistinguible entre unos y otros.
        return typeof this.value === 'object' && this.value !== null
            ? JSON.stringify(this.value)
            : String(this.value);
    }
}
// ─── Common Value Objects ──────────────────────────────────────────────────
export class TenantId extends ValueObject {
    validate(value) {
        if (!value || value.trim().length === 0) {
            throw new Error('TenantId cannot be empty');
        }
        if (!/^[a-zA-Z0-9_-]+$/.test(value)) {
            throw new Error('TenantId can only contain alphanumeric characters, hyphens and underscores');
        }
    }
    static of(value) {
        return new TenantId(value);
    }
}
export class PersonId extends ValueObject {
    validate(value) {
        if (!value || value.trim().length === 0) {
            throw new Error('PersonId cannot be empty');
        }
    }
    static of(value) {
        return new PersonId(value);
    }
}
export class Email extends ValueObject {
    static EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    validate(value) {
        if (!Email.EMAIL_REGEX.test(value)) {
            throw new Error(`Invalid email format: ${value}`);
        }
    }
    static of(value) {
        return new Email(value.toLowerCase().trim());
    }
}
/** El dominio de los correos PROVISORIOS: los de personas importadas sin una
 *  dirección real y única (`l-000123@no-email.invalid`). `.invalid` está
 *  reservado (RFC 2606): por norma nunca resuelve, así que un correo a esa
 *  dirección no le llega a nadie.
 *
 *  El correo sigue siendo obligatorio en `Person`; esto marca los que no son de
 *  verdad para que nada intente darles una cuenta con ellos. */
export const PLACEHOLDER_EMAIL_DOMAIN = 'no-email.invalid';
export function isPlaceholderEmail(email) {
    return email.trim().toLowerCase().endsWith(`@${PLACEHOLDER_EMAIL_DOMAIN}`);
}
export class PhoneNumber extends ValueObject {
    validate(value) {
        if (value.length > 0 && !/^\+?[0-9\s\-().]{7,20}$/.test(value)) {
            throw new Error(`Invalid phone number: ${value}`);
        }
    }
    static of(value) {
        return new PhoneNumber(value);
    }
}
export class DateOnly extends ValueObject {
    // ISO 8601 date string YYYY-MM-DD
    validate(value) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
            throw new Error(`Invalid date format (expected YYYY-MM-DD): ${value}`);
        }
        const d = new Date(value);
        if (isNaN(d.getTime())) {
            throw new Error(`Invalid date: ${value}`);
        }
    }
    static of(value) {
        return new DateOnly(value);
    }
    static today() {
        return new DateOnly(new Date().toISOString().slice(0, 10));
    }
    isAfter(other) {
        return this.value > other.value;
    }
    isBefore(other) {
        return this.value < other.value;
    }
}
/** TMAI Credit Unit (Phase E — CAP). Derived from student dedication hours
 *  via the framework's hours-per-TCU standard; may be fractional. */
export class Tcu extends ValueObject {
    validate(value) {
        if (!Number.isFinite(value)) {
            throw new Error(`Tcu must be a finite number: ${value}`);
        }
        if (value < 0) {
            throw new Error(`Tcu cannot be negative: ${value}`);
        }
    }
    static of(value) {
        return new Tcu(value);
    }
}
export class Money extends ValueObject {
    validate(value) {
        if (!Number.isFinite(value.amount) || value.amount < 0) {
            throw new Error(`Invalid money amount: ${value.amount}`);
        }
        if (!/^[A-Z]{3}$/.test(value.currency)) {
            throw new Error(`Invalid currency code: ${value.currency}`);
        }
    }
    static of(amount, currency) {
        return new Money({ amount, currency });
    }
    get amount() {
        return this.value.amount;
    }
    get currency() {
        return this.value.currency;
    }
    add(other) {
        if (this.currency !== other.currency) {
            throw new Error('Cannot add money with different currencies');
        }
        return Money.of(this.amount + other.amount, this.currency);
    }
}
//# sourceMappingURL=index.js.map